import { request } from './http'
import { openSSE, type SSEHandlers } from './sse'
import { ALL_PROJECTS_VALUE } from '@/lib/projectSelection'
import type { Asset, Chapter, ChapterImage, ChapterOutline } from '@/types/nf'
import type { CascadeActionResponse } from '@/types/pulse'
import { parseChapterQualitySnapshot, type ChapterQualitySnapshot } from '@/components/quality/qualityGate'
import type { JobChapterMode } from '@/constants/chapterModes'

interface ChapterListResponse {
  chapters: Chapter[] | null
}

interface ChapterPagedResponse {
  items: Chapter[] | null
  total: number
  page: number
  page_size: number
}

interface ChapterAssetsResponse {
  assets: Asset[] | null
}

interface ChapterQualityHistoryResponse {
  items: unknown[] | null
}

export type PartialRegenerateLengthMode = 'similar' | 'expand' | 'condense' | 'custom' | 'freeform'
export type ChapterGenerateMode = JobChapterMode
export type ChapterGenerateLang = 'zh' | 'en' | string

export interface GenerateChapterRequest {
  project_id: string
  outline: string
  chapter_number?: number
  title?: string
  chapter_mode?: ChapterGenerateMode
  target_words?: number
  lang?: ChapterGenerateLang
}

export interface GenerateChapterCreditEstimate {
  user_id: string
  project_id: string
  estimated_credits: number
  balance_credits: number
  reserved_credits: number
  available_credits: number
  sufficient: boolean
}

export interface SaveChapterOutlineRequest {
	 title?: string
	 target_words?: number
  summary: string
  scene_beats: string[]
  key_events: string[]
  characters: string[]
  volume_no?: number
  branch_id?: string
}

export type ChapterLibraryStatus = 'all' | 'done' | 'stopped' | 'awaiting_confirm'

export interface ChapterPagedParams {
  branchId?: string
  status?: ChapterLibraryStatus
  page: number
  pageSize: number
}

export interface ChapterPagedResult {
  items: Chapter[]
  total: number
  page: number
  pageSize: number
}

export interface PartialRegenerateRequest {
  start: number
  end: number
  selected_text: string
  instruction?: string
  length_mode: PartialRegenerateLengthMode
  target_chars?: number
  lang?: string
}

export interface ChapterTextPatch {
  id: string
  project_id?: string
  branch_id?: string
  chapter_id: string
  chapter_no?: number
  start_rune: number
  end_rune: number
  selected_text: string
  instruction?: string | null
  length_mode: PartialRegenerateLengthMode
  before_text?: string
  after_text?: string
  status: 'proposed' | 'confirmed' | 'discarded'
  created_at: string | number
  confirmed_at?: string | number | null
}

interface ChapterTextPatchesResponse {
  patches: ChapterTextPatch[] | null
}

export interface ConfirmPatchResponse {
  chapter_id: string
  new_word_count: number
  status: string
}

export interface ChapterVersionSummary {
  id: number
  version_no: number
  source: 'draft' | 'refine' | 'fork' | 'manual' | 'partial_regenerate' | 'regenerate' | 'restore' | string
  status: 'active' | 'candidate' | 'superseded' | 'discarded' | string
  model_key?: string
  created_at: string
  activated_at?: string
  usage_summary?: Record<string, number>
}

export interface ChapterVersionDetail extends ChapterVersionSummary {
  content: string
  word_count?: number
}

interface ChapterVersionsResponse {
  versions: ChapterVersionSummary[] | null
}

export interface RefineDonePayload {
  version_id: number
  status: 'candidate' | string
  content: string
  model_key?: string
}

export const chapterApi = {
	 nextDefaults: (projectId: string, branchId = 'main'): Promise<ChapterNextDefaults> =>
	   request(`/v1/projects/${encodeURIComponent(projectId)}/chapters/next-defaults?branch_id=${encodeURIComponent(branchId)}`),
  list: async (projectId: string, branchId?: string): Promise<Chapter[]> => {
    const qs = new URLSearchParams({ project_id: projectId === ALL_PROJECTS_VALUE ? '' : projectId })
    if (branchId) qs.set('branch_id', branchId)
    const data = await request<ChapterListResponse>(`/v1/chapters?${qs.toString()}`)
    return data.chapters ?? []
  },

  listPaged: async (projectId: string, params: ChapterPagedParams): Promise<ChapterPagedResult> => {
    const qs = new URLSearchParams({ project_id: projectId === ALL_PROJECTS_VALUE ? '' : projectId })
    if (params.branchId) qs.set('branch_id', params.branchId)
    if (params.status) qs.set('status', params.status)
    qs.set('page', String(params.page))
    qs.set('page_size', String(params.pageSize))
    const data = await request<ChapterPagedResponse>(`/v1/chapters?${qs.toString()}`)
    return {
      items: data.items ?? [],
      total: data.total,
      page: data.page,
      pageSize: data.page_size,
    }
  },

  get: (id: string): Promise<Chapter> => request<Chapter>(`/v1/chapters/${encodeURIComponent(id)}`),

  getOutline: (projectId: string, chapterNo: number, branchId?: string): Promise<ChapterOutline> => {
    const qs = new URLSearchParams()
    if (branchId) qs.set('branch_id', branchId)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<ChapterOutline>(`/v1/projects/${encodeURIComponent(projectId)}/outlines/${chapterNo}${suffix}`)
  },

  saveOutline: (projectId: string, chapterNo: number, body: SaveChapterOutlineRequest): Promise<ChapterOutline> =>
    request<ChapterOutline>(`/v1/projects/${encodeURIComponent(projectId)}/outlines/${chapterNo}`, {
      method: 'PUT',
      body,
    }),

  confirmOutline: (projectId: string, chapterNo: number, branchId?: string): Promise<ChapterOutline> => {
    const qs = new URLSearchParams()
    if (branchId) qs.set('branch_id', branchId)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return request<ChapterOutline>(`/v1/projects/${encodeURIComponent(projectId)}/outlines/${chapterNo}/confirm${suffix}`, {
      method: 'POST',
    })
  },

  assets: async (chapterId: string): Promise<Asset[]> => {
    const data = await request<ChapterAssetsResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/assets`)
    return data.assets ?? []
  },

  qualityHistory: async (chapterId: string, projectId: string): Promise<ChapterQualitySnapshot[]> => {
    const qs = new URLSearchParams({ project_id: projectId })
    const data = await request<ChapterQualityHistoryResponse>(
      `/v1/chapters/${encodeURIComponent(chapterId)}/quality/history?${qs.toString()}`,
    )
    return (data.items ?? []).map(parseChapterQualitySnapshot).filter((item): item is ChapterQualitySnapshot => Boolean(item))
  },

  images: (chapterId: string): Promise<ChapterImage[]> => request<ChapterImage[]>(`/v1/chapters/${encodeURIComponent(chapterId)}/images`),

  partialRegenerateStream: (chapterId: string, body: PartialRegenerateRequest, handlers: SSEHandlers): (() => void) =>
    openSSE(`/v1/chapters/${encodeURIComponent(chapterId)}/partial-regenerate/stream`, handlers, {
      method: 'POST',
      body,
      maxRetries: 0,
    }),

  generateStream: (body: GenerateChapterRequest, handlers: SSEHandlers): (() => void) =>
    openSSE('/v1/chapters/generate/stream', handlers, {
      method: 'POST',
      body,
      maxRetries: 3,
      baseDelayMs: 800,
    }),

  estimateGenerate: (body: GenerateChapterRequest): Promise<GenerateChapterCreditEstimate> =>
    request<GenerateChapterCreditEstimate>('/v1/chapters/generate/estimate', {
      method: 'POST',
      body,
    }),

  refineStream: (chapterId: string, projectId: string, lang: string | undefined, handlers: SSEHandlers): (() => void) => {
    const qs = new URLSearchParams({ project: projectId })
    if (lang) qs.set('lang', lang)
    return openSSE(`/v1/chapters/${encodeURIComponent(chapterId)}/refine?${qs.toString()}`, handlers, {
      method: 'POST',
      headers: { 'Idempotency-Key': crypto.randomUUID() },
      maxRetries: 0,
    })
  },

  listVersions: async (chapterId: string, projectId: string): Promise<ChapterVersionSummary[]> => {
    const qs = new URLSearchParams({ project: projectId })
    const data = await request<ChapterVersionsResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/versions?${qs.toString()}`)
    return data.versions ?? []
  },

  getVersion: (chapterId: string, versionId: number, projectId: string): Promise<ChapterVersionDetail> => {
    const qs = new URLSearchParams({ project: projectId })
    return request<ChapterVersionDetail>(`/v1/chapters/${encodeURIComponent(chapterId)}/versions/${versionId}?${qs.toString()}`)
  },

  adoptVersion: (chapterId: string, versionId: number, projectId: string): Promise<void> => {
    const qs = new URLSearchParams({ project: projectId })
    return request<void>(`/v1/chapters/${encodeURIComponent(chapterId)}/versions/${versionId}/adopt?${qs.toString()}`, {
      method: 'POST',
    })
  },

  discardVersion: (chapterId: string, versionId: number, projectId: string): Promise<void> => {
    const qs = new URLSearchParams({ project: projectId })
    return request<void>(`/v1/chapters/${encodeURIComponent(chapterId)}/versions/${versionId}/discard?${qs.toString()}`, {
      method: 'POST',
    })
  },

  restoreVersion: (chapterId: string, versionId: number, projectId: string): Promise<void> => {
    const qs = new URLSearchParams({ project: projectId })
    return request<void>(`/v1/chapters/${encodeURIComponent(chapterId)}/versions/${versionId}/restore?${qs.toString()}`, {
      method: 'POST',
    })
  },

  resubmit: (chapterId: string): Promise<void> =>
    request<void>(`/v1/chapters/${encodeURIComponent(chapterId)}/resubmit`, { method: 'POST' }),

  reviewAsset: (chapterId: string, assetId: string, action: 'approve' | 'reject', reason: string) =>
    request<{asset_id: string; status: string}>(`/v1/chapters/${chapterId}/assets/${assetId}/review`, { method: 'POST', body: {action, reason} }),

  continueCascade: (chapterId: string): Promise<CascadeActionResponse> =>
    request<CascadeActionResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/cascade/continue`, { method: 'POST' }),

  stopCascade: (chapterId: string): Promise<CascadeActionResponse> =>
    request<CascadeActionResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/cascade/stop`, { method: 'POST' }),

  confirmPatch: (chapterId: string, patchId: string): Promise<ConfirmPatchResponse> =>
    request<ConfirmPatchResponse>(
      `/v1/chapters/${encodeURIComponent(chapterId)}/patches/${encodeURIComponent(patchId)}/confirm`,
      { method: 'POST' },
    ),

  discardPatch: (chapterId: string, patchId: string): Promise<void> =>
    request<void>(`/v1/chapters/${encodeURIComponent(chapterId)}/patches/${encodeURIComponent(patchId)}/discard`, {
      method: 'POST',
    }),

  listPatches: async (chapterId: string): Promise<ChapterTextPatch[]> => {
    const data = await request<ChapterTextPatchesResponse>(`/v1/chapters/${encodeURIComponent(chapterId)}/patches`)
    return data.patches ?? []
  },
}

export interface ChapterNextDefaults {
  chapter_no: number
  volume_no: number
  target_words: number
  chapter_mode: import('@/constants/chapterModes').JobChapterMode
  outline: string
  outline_draft?: ChapterOutline
}
